import * as fs from 'fs';
import * as path from 'path';
import { colors } from './const';
import { resolveProjectRoot } from './resolve-project-root';
import {
  formatInput5HandoffBlocks,
  formatDeployTargetLabel,
} from './workflow-prompts';
import type { DeployTargetLabel } from './workflow-prompts';
import {
  readWorkflowState,
  recordDeployConfirmed,
} from './workflow-state';
import { fetchSurveyFromEnv } from './fetch-survey-inventory';
import { getErrorMessage } from '../types/shared';

const c = colors;

export const SURVEY_DEPLOY_CONFIG_FILE = 'survey-deploy-config.json';
const LEGACY_SURVEY_UI_CONFIG_FILE = 'survey-ui-config.json';

export type DeployTarget = DeployTargetLabel;

function deployConfigPath(projectRoot: string): string {
  return path.join(projectRoot, SURVEY_DEPLOY_CONFIG_FILE);
}

function legacyConfigPath(projectRoot: string): string {
  return path.join(projectRoot, LEGACY_SURVEY_UI_CONFIG_FILE);
}

/** Resolve deploy config file — prefers survey-deploy-config.json, falls back to legacy name. */
function resolveConfigFile(projectRoot: string): string {
  const deployPath = deployConfigPath(projectRoot);
  if (fs.existsSync(deployPath)) {
    return deployPath;
  }
  const legacyPath = legacyConfigPath(projectRoot);
  if (fs.existsSync(legacyPath)) {
    return legacyPath;
  }
  return deployPath;
}

/** Read deploy.target from project-root survey-deploy-config.json (fixed path, no search). */
export function readDeployTarget(projectRoot: string): DeployTarget {
  const abs = resolveConfigFile(projectRoot);
  if (!fs.existsSync(abs)) {
    throw new Error(
      `${SURVEY_DEPLOY_CONFIG_FILE} not found — run npx cfm-sdk seed-phase5 first`,
    );
  }

  const raw = JSON.parse(fs.readFileSync(abs, 'utf8')) as {
    deploy?: { target?: string };
  };
  return raw.deploy?.target === 'vercel' ? 'vercel' : 'aws';
}

/** Patch deploy.target only — writes survey-deploy-config.json; migrates from legacy file if needed. */
export function writeDeployTarget(projectRoot: string, target: DeployTarget): string {
  const outPath = deployConfigPath(projectRoot);
  const legacyPath = legacyConfigPath(projectRoot);

  let raw: Record<string, unknown> = { deploy: { target: 'aws', autoDeploy: false } };
  if (fs.existsSync(outPath)) {
    raw = JSON.parse(fs.readFileSync(outPath, 'utf8')) as Record<string, unknown>;
  } else if (fs.existsSync(legacyPath)) {
    raw = JSON.parse(fs.readFileSync(legacyPath, 'utf8')) as Record<string, unknown>;
  }

  const deploy = (raw.deploy as Record<string, unknown> | undefined) ?? {};
  deploy.target = target;
  if (deploy.autoDeploy === undefined) {
    deploy.autoDeploy = false;
  }

  const next = {
    deploy: {
      target: deploy.target,
      autoDeploy: deploy.autoDeploy,
    },
  };

  fs.writeFileSync(outPath, `${JSON.stringify(next, null, 2)}\n`, 'utf8');
  return outPath;
}

export interface GetDeployTargetOptions {
  projectRoot?: string;
}

export function runGetDeployTarget(options: GetDeployTargetOptions = {}): number {
  const projectRoot = resolveProjectRoot(options.projectRoot);
  const target = readDeployTarget(projectRoot);
  const label = formatDeployTargetLabel(target);

  console.log(`${c.green}deploy.target:${c.reset} ${target}`);
  console.log(`${c.dim}Platform: ${label} (from ${SURVEY_DEPLOY_CONFIG_FILE})${c.reset}`);
  return 0;
}

export interface SetDeployTargetOptions {
  projectRoot?: string;
  target?: string;
  surveyId?: string;
}

export async function runSetDeployTarget(options: SetDeployTargetOptions = {}): Promise<number> {
  const raw = options.target?.toLowerCase().trim();
  if (raw !== 'aws' && raw !== 'vercel') {
    console.error(`${c.red}FAIL:${c.reset} Usage: npx cfm-sdk set-deploy-target aws|vercel`);
    return 1;
  }

  const projectRoot = resolveProjectRoot(options.projectRoot);

  const state = readWorkflowState(projectRoot);
  if (!state?.coreVerifyPassedAt) {
    console.error(
      `\n${c.red}Core verify not recorded. Run npx cfm-sdk verify-build successfully before set-deploy-target.${c.reset}`,
    );
    return 1;
  }

  const configFile = writeDeployTarget(projectRoot, raw);
  const label = formatDeployTargetLabel(raw);

  try {
    recordDeployConfirmed(projectRoot);
  } catch (err: unknown) {
    console.error(`\n${c.red}${getErrorMessage(err)}${c.reset}`);
    return 1;
  }

  console.log(
    `${c.green}OK:${c.reset} Wrote deploy.target=${raw} (${label}) → ${configFile}`,
  );

  if (raw === 'aws') {
    let surveyId = options.surveyId?.trim() || '<survey-id>';
    const instanceId = process.env.CFM_INSTANCE_ID?.trim();
    if (instanceId && surveyId === '<survey-id>') {
      try {
        const { survey } = await fetchSurveyFromEnv(projectRoot, instanceId);
        surveyId = survey.id;
      } catch {
        // keep placeholder — create-distribution will show real ID if key missing
      }
    }
    console.log(`\n${formatInput5HandoffBlocks(surveyId)}\n`);
    console.log(
      `${c.dim}After client pastes key: npx cfm-sdk create-distribution --s3-key=<key>${c.reset}`,
    );
  } else {
    console.log(`${c.dim}After client confirms deploy yes: npx cfm-sdk deploy${c.reset}`);
  }

  return 0;
}
